import styled from 'styled-components';
import { Layout, Menu, Flex, Drawer } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { colors, fonts, sizes, breakpoints } from '../../theme';

const { Header: AntHeader } = Layout;

export const StyledHeader = styled(AntHeader)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${colors.black};
  padding: ${sizes.padding.sm} 0;
  border-bottom: ${sizes.dimensions.borderThick} solid transparent;
  border-image: ${colors.gradients.borderGradient} 1;

  &::after {
    content: '';
    position: absolute;
    bottom: -${sizes.dimensions.shadowHeight};
    left: 0;
    right: 0;
    height: ${sizes.dimensions.shadowHeight};
    background: ${colors.gradients.shadowFade};
  }

  ${breakpoints.media.mobile} {
    padding: ${sizes.padding.xs} 0;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: ${sizes.layout.maxWidth};
  margin: ${sizes.margin.auto};
  padding: 0 ${sizes.padding.lg};

  ${breakpoints.media.mobile} {
    padding: 0 ${sizes.padding.sm};
  }
`;

export const StyledFlex = styled(Flex)`
  width: 100%;
`;

export const HeaderLogo = styled.div`
  flex: 0 0 auto;
`;

export const HeaderLogoImg = styled.img`
  height: ${sizes.dimensions.logoDesktop};
  width: auto;
  cursor: pointer;
  transition: transform 0.3s ease, filter 0.3s ease;

  &:hover {
    transform: scale(1.1);
    filter: drop-shadow(0 0 ${sizes.pixels.medium} ${colors.whiteTransparent[30]});
  }

  ${breakpoints.media.mobile} {
    height: ${sizes.dimensions.logoMobile};
  }
`;

export const DesktopNavigation = styled(Menu)`
  display: flex;
  align-items: center;
  gap: ${sizes.gap.lg};
  flex: 1;
  justify-content: center;
  background: transparent !important;
  border: none !important;

  .ant-menu-item {
    background: none !important;
    border: none !important;
    color: ${colors.white} !important;
    font-family: ${fonts.families.primary} !important;
    font-size: ${fonts.sizes.responsive} !important;
    cursor: pointer !important;
    padding: ${sizes.padding.button} !important;
    border-radius: ${sizes.borderRadius.small} !important;
    transition: all 0.3s ease !important;
    text-transform: ${fonts.textTransform.uppercase} !important;
    letter-spacing: ${fonts.letterSpacing.wide} !important;
    position: relative !important;
    overflow: hidden !important;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      background: ${colors.gradients.primaryHorizontal};
      transition: all 0.3s ease;
      transform: translateX(-50%);
    }

    &:hover {
      color: ${colors.primary} !important;
      text-shadow: 0 0 ${sizes.pixels.medium} ${colors.primaryTransparent[50]} !important;
      transform: translateY(-2px) !important;
      background: none !important;

      &::before {
        width: 100%;
      }
    }

    span {
      font-family: ${fonts.families.primary} !important;
      color: inherit !important;
    }
  }

  .ant-menu-item-selected {
    background: none !important;
    color: ${colors.primary} !important;

    &::after {
      border-bottom: 2px solid ${colors.primary} !important;
      background: ${colors.gradients.primaryHorizontal} !important;
    }
  }

  .ant-menu-item-active {
    background: none !important;
    color: ${colors.primary} !important;

    &::after {
      border-bottom: 2px solid ${colors.primary} !important;
      background: ${colors.gradients.primaryHorizontal} !important;
    }
  }

  .ant-menu-item:hover::after {
    background: ${colors.gradients.primaryHorizontal} !important;
    border-bottom: 2px solid ${colors.primary} !important;
  }

  .ant-menu-item-selected,
  .ant-menu-item-active,
  .ant-menu-item:hover {
    border-bottom: none !important;
    border-color: transparent !important;
  }

  ${breakpoints.media.mobile} {
    display: none !important;
  }
`;

export const HeaderLanguageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.gap.xs};
  flex: 0 0 auto;

  ${breakpoints.media.mobile} {
    /* Keep visible on mobile */
  }
`;

export const MobileMenuContainer = styled.div`
  display: none;

  ${breakpoints.media.mobile} {
    display: flex;
    align-items: center;
  }
`;

// === HAMBURGER MENU STYLED COMPONENTS ===

export const StyledDrawer = styled(Drawer)`
  .ant-drawer-content {
    background: ${colors.black} !important;
    border-left: ${sizes.dimensions.borderThick} solid ${colors.primary};
  }

  .ant-drawer-header {
    background: ${colors.black} !important;
    border-bottom: ${sizes.dimensions.borderStandard} solid ${colors.primaryTransparent[30]};
    padding: ${sizes.padding.sm};
    
    .ant-drawer-title {
      color: ${colors.white} !important;
      font-family: ${fonts.families.primary} !important;
      font-size: ${fonts.sizes.lg} !important;
      text-transform: ${fonts.textTransform.uppercase} !important;
      letter-spacing: ${fonts.letterSpacing.wide} !important;
    }
    
    .ant-drawer-close {
      color: ${colors.white} !important;
      font-size: ${fonts.sizes.lg} !important;
      
      &:hover {
        color: ${colors.primary} !important;
      }
    }
  }

  .ant-drawer-body {
    padding: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    background: ${colors.black} !important;
  }
`;

export const StyledNavigationList = styled.div`
  background: transparent;
  border: none;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${sizes.padding.drawerTop};
  min-height: ${sizes.dimensions.navigationMinHeight};
`;

export const NavigationItem = styled.div<{ isActive?: boolean }>`
  background: ${props => props.isActive ? colors.primaryTransparent[25] : colors.blackTransparent[30]};
  border: ${sizes.dimensions.borderStandard} solid ${props => props.isActive ? colors.primary : colors.primaryTransparent[30]};
  color: ${props => props.isActive ? colors.primary : colors.white};
  font-family: ${fonts.families.primary};
  font-size: ${fonts.sizes.md};
  font-weight: ${fonts.weights.normal};
  cursor: pointer;
  padding: ${sizes.padding.hamburgerItem};
  margin: ${sizes.margin.hamburgerItem};
  border-radius: ${sizes.borderRadius.small};
  transition: all 0.3s ease;
  text-transform: ${fonts.textTransform.uppercase};
  letter-spacing: ${fonts.letterSpacing.wide};
  position: relative;
  overflow: visible;
  border-left: ${sizes.dimensions.borderWidth} solid ${props => props.isActive ? colors.primary : colors.primaryTransparent[70]};
  height: auto;
  line-height: ${fonts.lineHeight.relaxed};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: ${sizes.dimensions.hamburgerItem};
  width: 100%;
  box-sizing: border-box;
  z-index: 1;
  text-shadow: ${props => props.isActive ? `0 0 10px ${colors.primaryTransparent[70]}` : 'none'};
  transform: ${props => props.isActive ? 'translateX(5px)' : 'none'};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: ${props => props.isActive ? '100%' : '0'};
    height: 100%;
    background: ${colors.gradients.activeBackground};
    transition: all 0.3s ease;
    z-index: -1;
  }

  &:hover {
    color: ${colors.primary};
    background: ${colors.primaryTransparent[15]};
    border-left-color: ${colors.primary};
    text-shadow: 0 0 10px ${colors.primaryTransparent[50]};
    transform: translateX(5px);
    
    &::before {
      width: 100%;
    }
  }
`;

export const LanguageSelectorContainer = styled.div`
  padding: ${sizes.padding.sm};
  border-top: ${sizes.dimensions.borderStandard} solid ${colors.primaryTransparent[30]};
  background: ${colors.blackTransparent[30]};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  box-sizing: border-box;

  .language-selector-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .language-selector-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    gap: ${sizes.gap.xs};
    background: ${colors.blackTransparent[20]};
    border-radius: ${sizes.borderRadius.large};
    padding: ${sizes.pixels.tiny};
    border: ${sizes.dimensions.borderStandard} solid ${colors.primaryTransparent[30]};
  }

  .language-selector-indicator {
    position: absolute;
    top: ${sizes.pixels.tiny};
    left: ${sizes.pixels.tiny};
    width: ${sizes.dimensions.languageSelector};
    height: calc(100% - ${sizes.pixels.small});
    background: ${colors.gradients.primaryLinear};
    border-radius: ${sizes.borderRadius.medium};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px ${sizes.pixels.medium} ${colors.primaryTransparent[40]};
    z-index: 1;
  }

  .language-selector-indicator.slide-left {
    transform: translateX(0);
  }

  .language-selector-indicator.slide-right {
    transform: translateX(calc(${sizes.dimensions.languageSelector} + ${sizes.gap.xs} + ${sizes.pixels.languageSelectorOffset} + ${sizes.gap.xs}));
  }

  .language-btn {
    background: none;
    border: none;
    color: ${colors.white};
    font-family: ${fonts.families.secondary};
    font-size: ${fonts.sizes.xxl};
    font-weight: ${fonts.weights.bold};
    cursor: pointer;
    padding: ${sizes.padding.languageBtn};
    border-radius: ${sizes.borderRadius.medium};
    transition: all 0.3s ease;
    min-width: ${sizes.dimensions.languageSelector};
    position: relative;
    z-index: 2;
  }

  .language-btn:hover {
    color: ${colors.white};
    transform: scale(1.05);
    background: none;
    text-shadow: 0 0 ${sizes.pixels.medium} ${colors.whiteTransparent[50]};
  }

  .language-btn.active {
    color: ${colors.white};
    background: none;
    text-shadow: 0 0 ${sizes.pixels.medium} ${colors.whiteTransparent[80]};
  }

  .language-separator {
    color: ${colors.white};
    font-size: 18px;
    opacity: 0.7;
    font-family: ${fonts.families.secondary};
    position: relative;
    z-index: 2;
  }

  .ant-segmented {
    font-size: ${fonts.sizes.xs} !important;
    min-height: ${sizes.dimensions.segmentedMinHeight} !important;
  }

  .ant-segmented-item {
    padding: ${sizes.padding.segmented} !important;
    min-height: ${sizes.dimensions.segmentedMinHeight} !important;
    font-size: ${fonts.sizes.xs} !important;
  }
`;

export const HamburgerIcon = styled(MenuOutlined)`
  color: ${colors.white};
  font-size: ${fonts.sizes.xl};
  cursor: pointer;
  padding: ${sizes.padding.xs};
  border-radius: ${sizes.borderRadius.small};
  transition: all 0.3s ease;

  &:hover {
    color: ${colors.primary};
    background: ${colors.primaryTransparent[10]};
    transform: scale(1.1);
  }
`;